import {
  Box,
  Button,
  DialogContent,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import {
  AlertBanner,
  AmountField,
  BaseDialog,
  SelectField,
  StyledTextField,
} from '@resurs-internal/mui';
import {
  tryParseFloat,
  useCountry,
  useCustomer,
  useDepositAccount,
  useDepositAccounts,
} from '@resurs-internal/rb-app-api';
import {useEffect, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslate} from 'hooks';
import {useForm} from 'react-hook-form';
import {useDialog} from 'hooks/useDialog/useDialog';
import {SavingsAccountWithdrawalAlertBanner} from './SavingsAccountWithdrawalAlertBanner';
import {OpenBankingDisbursementDialogContent} from './OpenBankingDisbursementDialog';
import {storeObDisbursementSession} from './utils/disbursementSessionStorage';
import {useDepositOpenBankingAccounts} from '@resurs-internal/rb-app-api/features/deposit/hooks/useDepositOpenBankingAccounts/useDepositOpenBankingAccounts';
import {createRedirectPath} from '../../../services/uri/createRedirectPath';
import {DisbursementAccountDisplay} from '@resurs-internal/deposits-app-components';
import {maskAccountNumber} from '../utils/maskAccountNumber';
import {VisibilityLoading} from '../../../components';
import {useWithdrawalSubmit} from './hooks/useWithdrawalSubmit';
import {DISBURSEMENT_EXCHANGE_QUERY_KEY} from './hooks/useDisbursementExchange';
import {getDepositAccountLabel} from '../utils/depositAccountLabel';

export interface SendMoneyFormValues {
  amount: string;
  message: string;
  recipientName: string;
}

export type SavingsWithdrawalDialogState = {accountId: string};

export const SavingsAccountWithdrawalDialog = () => {
  const translate = useTranslate();
  const country = useCountry();
  const customer = useCustomer();
  const dialog = useDialog<SavingsWithdrawalDialogState>('savings-withdrawal');
  const paramsId = dialog.state?.accountId;

  const accounts = useDepositAccounts();
  const accountsData = accounts.query.data?.accounts || [];
  const [_fromAccountId, setFromAccountId] = useState<string>();
  const fromAccountId =
    _fromAccountId ?? paramsId ?? accountsData[0]?.accountId ?? '';
  const account = useDepositAccount(fromAccountId);
  const fromAccount = account.query.data;

  const toAccount = fromAccount?.disbursementAccount;
  const [view, setView] = useState<'form' | 'connect'>('form');
  const [connectError, setConnectError] = useState(false);
  const [connectRedirecting, setConnectRedirecting] = useState(false);
  const {authenticate} = useDepositOpenBankingAccounts();
  const queryClient = useQueryClient();
  const connectLoading = authenticate.isPending || connectRedirecting;
  const isDK = country.code === 'DK';
  const showUpdateWithdrawalReceiver =
    !isDK && Boolean(fromAccount?.capabilities.changeDisbursementAccount);

  const {submit} = useWithdrawalSubmit({
    accountId: fromAccountId,
    toAccount,
    onClose: dialog.onClose,
  });

  const {
    register,
    handleSubmit,
    watch,
    unregister,
    formState: {errors, isValid, isSubmitting},
  } = useForm({
    mode: 'all',
    shouldUnregister: true,
    defaultValues: {
      amount: '',
      message: '',
      recipientName: '',
    },
  });

  const amount = watch('amount');

  useEffect(() => {
    if (country.code !== 'SE') {
      unregister('recipientName');
    }
  }, [country.code, unregister]);

  const disabled =
    !isValid ||
    !fromAccount ||
    !toAccount ||
    !fromAccount.capabilities.withdrawal ||
    isSubmitting;

  const handleClose = () => {
    if (view === 'connect') {
      setView('form');
      return;
    }
    dialog.onClose();
  };

  const handleConnect = async () => {
    if (!fromAccountId) return;
    setConnectError(false);

    try {
      const redirectUrl = createRedirectPath(
        '/withdrawal/openbanking/callback',
        {accountId: fromAccountId},
      );

      const response = await authenticate.mutateAsync({redirectUrl});

      // The exchange result is cached forever (staleTime: Infinity), so drop
      // the previous one or the next exchange would show its accounts.
      queryClient.removeQueries({queryKey: DISBURSEMENT_EXCHANGE_QUERY_KEY});
      storeObDisbursementSession({
        applicantId: response.applicantId,
        sessionId: response.sessionId,
        accountId: fromAccountId,
      });

      const authUrl = response._links?.auth?.href;
      if (authUrl) {
        setConnectRedirecting(true);
        window.location.href = authUrl;
      } else {
        setConnectError(true);
      }
    } catch {
      setConnectError(true);
    }
  };

  const onSubmit = handleSubmit(submit);

  const hasDisbursementAccount = Boolean(toAccount?.accountNumber);

  const getDisbursementDisplayTitle = () => {
    if (!toAccount?.accountNumber) return '';
    return maskAccountNumber(toAccount.accountNumber);
  };

  const getDisbursementDisplaySubtitle = () => {
    return toAccount?.accountPrefix ?? '';
  };

  return (
    <BaseDialog
      header={translate(
        view === 'connect'
          ? 'savings.openbanking.disbursement.dialog.title'
          : 'savings.withdraw.title',
      )}
      onClose={handleClose}
      open={dialog.open}
      maxWidth="sm"
      actions={
        view === 'connect'
          ? [
              {
                color: 'primary',
                title: translate(
                  'savings.openbanking.disbursement.dialog.connect',
                ),
                disabled: !fromAccountId || connectLoading,
                onClick: handleConnect,
              },
            ]
          : undefined
      }>
      {view === 'connect' ? (
        <OpenBankingDisbursementDialogContent
          accountId={fromAccountId}
          isLoading={connectLoading}
          error={connectError}
        />
      ) : (
        <DialogContent>
          <VisibilityLoading isLoading={accounts.query.isPending}>
            <Stack direction={'column'} spacing={3}>
              {country.code === 'NO' && (
                <AlertBanner
                  severity={'info'}
                  title={translate('info.savings.withdrawal.disallowed.title')}
                  subtitle={translate(
                    'info.savings.withdrawal.disallowed.subtitle',
                  )}
                />
              )}
              <Box>
                <SelectField
                  label={translate('savings.send-money.from')}
                  value={fromAccountId}
                  disabled={!accountsData.length}
                  sx={{
                    '& .MuiSelect-select': {
                      whiteSpace: 'normal',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          paddingRight: 3,
                        },
                        '& .MuiListItemText-root': {
                          whiteSpace: 'normal',
                        },
                      },
                    },
                  }}
                  options={accountsData.map((account) => {
                    return {
                      id: account.accountId,
                      label: getDepositAccountLabel(
                        account,
                        customer.name,
                        country.formatAmount(account.currentBalance),
                      ),
                    };
                  })}
                  onChange={(event: SelectChangeEvent<unknown>) => {
                    setFromAccountId(event.target.value as string);
                  }}
                />
              </Box>
              {fromAccount &&
                !account.query.isFetching &&
                !fromAccount.capabilities.withdrawal && (
                  <AlertBanner
                    severity={'warning'}
                    title={translate(
                      'error.savings.withdrawal.disallowed.title',
                    )}
                    subtitle={translate(
                      'error.savings.withdrawal.disallowed.subtitle',
                    )}
                  />
                )}
              {isDK && hasDisbursementAccount ? (
                <StyledTextField
                  label={translate('savings.send-money.to')}
                  value={`Nemkonto ${toAccount?.displayValue ?? ''}`}
                  disabled
                  InputProps={{readOnly: true}}
                />
              ) : (
                <Stack gap={1}>
                  <Typography variant="caption" color="text.secondary">
                    {translate('savings.send-money.to')}
                  </Typography>
                  {hasDisbursementAccount ? (
                    <DisbursementAccountDisplay
                      title={getDisbursementDisplayTitle()}
                      subtitle={getDisbursementDisplaySubtitle()}
                      actionLabel={translate('common.change')}
                      onActionClick={() => setView('connect')}
                      disabled={!showUpdateWithdrawalReceiver}
                    />
                  ) : (
                    <DisbursementAccountDisplay
                      title={translate(
                        'savings.openbanking.disbursement.no-account',
                      )}
                      actionLabel={translate('common.add')}
                      onActionClick={() => setView('connect')}
                      disabled={!showUpdateWithdrawalReceiver}
                    />
                  )}
                </Stack>
              )}
              {country.code === 'SE' && (
                <StyledTextField
                  label={translate('savings.withdrawal.recipientName')}
                  type={'text'}
                  error={Boolean(errors.recipientName)}
                  helperText={errors.recipientName?.message}
                  {...register('recipientName', {
                    required: true,
                    pattern: /^[A-Za-z\u00C0-\u017F\u0100-\u024F '.-]+$/,
                    validate: (value) => {
                      if (value.trim().length <= 3 || value.trim().length > 70)
                        return translate(
                          'error.savings.withdrawal.recipientName.invalid',
                        );
                    },
                  })}
                />
              )}
              <Box>
                <AmountField
                  outline={'top'}
                  label={translate('common.amount')}
                  value={amount}
                  error={Boolean(errors.amount)}
                  helperText={errors.amount?.message}
                  formatNumber={(n) =>
                    country.formatAmount({
                      amount: n,
                      currency: country.currency,
                    })
                  }
                  {...register('amount', {
                    required: true,
                    validate: (s) => {
                      const number = tryParseFloat(s);
                      if (number <= 0) return translate('common.invalid-input');
                      if (number > (fromAccount?.currentBalance?.amount ?? 0)) {
                        return translate('payments.exceed.balance');
                      }
                    },
                  })}
                />
                <StyledTextField
                  outline={'bottom'}
                  label={
                    country.code === 'NO'
                      ? translate('savings.withdrawal.message-optional')
                      : translate('common.message-optional')
                  }
                  type={'text'}
                  error={Boolean(errors.message)}
                  helperText={errors.message?.message}
                  {...register('message', {
                    pattern: /^([A-ZÅÄÖÜ a-zåäö0-9]*)$/,
                    validate: (value) => {
                      if (value.length > 15)
                        return translate(
                          'error.savings.withdrawal.message.invalid',
                        );
                      if (
                        country.code === 'NO' &&
                        !/^([A-ZÅÄÖÜa-zåäö][A-ZÅÄÖÜ a-zåäö0-9]*)$/.test(
                          value,
                        ) &&
                        value.length > 0
                      ) {
                        return translate(
                          'error.savings.withdrawal.message.invalid-type',
                        );
                      }
                    },
                  })}
                />
              </Box>
              <SavingsAccountWithdrawalAlertBanner
                error={account.withdrawal.error}
              />
              <Button disabled={disabled} onClick={onSubmit}>
                {translate('common.sign')}
              </Button>
            </Stack>
          </VisibilityLoading>
        </DialogContent>
      )}
    </BaseDialog>
  );
};
